import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import {
    CreateChancel,
    Input,
    DateCalendarForm,
    SelectAutocompletePagination,
    TagComponent,
    SimpleTextEditor,
    SelectTypeAutocomplete, SelectAutocompleteMultiple,
} from 'components';
import { FindSuccess, FindLoad, FindErrorItem, useModal, renderStaffName, tagEnums, renderClientName } from 'utils';
import { taskActions, httpRequestsOnErrorsActions, httpRequestsOnSuccessActions, fundingSourceActions } from 'store';
import moment from 'moment';
import { renderColor } from '../../../components/tagFiltering/colors';
import { canManageTags, getAssignedTagIds } from 'store/tags/tag.utils';
import { taskOnModelList, taskOnModelEnums } from '../constants';

export const CreateTask = ({ info, afterSuccess }) => {
    const { tagList } = useSelector((state) => ({
        tagList: state.tags.tagList,
    }));
    const fSelect = useSelector((state) => state.fundingSource.fSelect);
    const payerList = Array.isArray(fSelect) ? fSelect : (fSelect?.funders || []);
    const dispatch = useDispatch();
    const { close } = useModal();
    const isEdit = !!info;
    const ACTION_TYPE = isEdit ? 'UPDATE_TASK' : 'CREATE_TASK';
    const loader = FindLoad(ACTION_TYPE);
    const success = FindSuccess(ACTION_TYPE);
    const backError = FindErrorItem(ACTION_TYPE);
    const { handleSubmit, watch, control, reset, setValue } = useForm();
    const [selectedTags, setSelectedTags] = useState([]);

    useEffect(() => {
        if (info) {
            reset({
                title: info.title || '',
                description: info.description || '',
                dueDate: info.dueDate || '',
                assignee: info.assignee?.id || null,
                reporter: info.reporter?.id || null,
                onModel: info.onModel || null,
                resourceId: info?.resource?.id || null,
            });
            setSelectedTags(getAssignedTagIds(info));
            if (info.onModel === taskOnModelEnums.FUNDER) {
                dispatch(fundingSourceActions.getFundingSource());
            }
        }
    }, [info]);


    useEffect(() => {
        if (!!success.length) {
            close();
            afterSuccess && afterSuccess();
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
        }
    }, [success]);

    useEffect(() => {
        return () => {
            dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
        };
    }, []);

    const handleToggleTag = (tagId) => {
        setSelectedTags((prev) => (prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId]));
    };

    const handleDateShortcut = (days) => {
        const date = moment().add(days, 'days').format('YYYY-MM-DD');
        setValue('dueDate', date);
    };

    function handleCreate(data) {
        const body = {
            title: data.title,
        };
        body.description = data.description ? data.description : '';
        body.assignee = data.assignee ? data.assignee : null;
        body.reporter = data.reporter ? data.reporter : null;
        body.dueDate = data.dueDate ? data.dueDate : null;
        body.onModel = data.onModel ? data.onModel : null;
        body.resourceId = data.resourceId ? data.resourceId : null;

        if (selectedTags.length) body.tags = selectedTags;

        if (isEdit) {
            dispatch(taskActions.updateTask(info?.id, body));
        } else {
            dispatch(taskActions.createTask(body));
        }
    }

    return (
        <form onSubmit={handleSubmit(handleCreate)} className="task-create-form">
            <p className={`modal-header-title task-create-header`}>{isEdit ? 'Edit Task' : 'Create New Task'}</p>
            {!isEdit &&
                <p className="task-create-subtitle">Add a new task to your list or assign it to a team member</p>}
            <div className="modal-body-wrapper" style={{ gap: 16 }}>
                <Input
                    name="title"
                    label="Task Title*"
                    variant="outlined"
                    control={control}
                    rules={{ required: 'Task title is required.' }}
                    maxLength={100}
                />
                <div className="full-width">
                    <p className="task-tags-label">Description</p>
                    <Controller
                        control={control}
                        name="description"
                        defaultValue={''}
                        render={({ field, fieldState: { error } }) => (
                            <SimpleTextEditor
                                value={field.value}
                                onChange={(val) => field.onChange(val)}
                                placeholder="Description..."
                                error={error}
                                border={'1px solid rgb(12, 102, 228)'}
                            />
                        )}
                    />
                </div>
                <div className="flex-align-center" style={{ gap: 16 }}>
                    <div className="full-width">
                        <SelectAutocompletePagination
                            uiType={'fromForm'}
                            title={'Assignee'}
                            name={'assignee'}
                            handleSelect={(ev) => setValue('assignee', ev?.target?.value)}
                            defaultValue={watch('assignee')}
                            renderValue={(i) => renderStaffName(i)}
                            type={'staff'}
                            currentSelected={info?.assignee && info?.assignee}
                        />
                    </div>
                    <div className="full-width">
                        <SelectAutocompletePagination
                            uiType={'fromForm'}
                            title={'Reporter'}
                            name={'reporter'}
                            handleSelect={(ev) => setValue('reporter', ev?.target?.value)}
                            defaultValue={watch('reporter')}
                            renderValue={(i) => renderStaffName(i)}
                            type={'staff'}
                            currentSelected={info?.reporter && info?.reporter}
                        />
                    </div>
                </div>
                <div className="flex-align-start" style={{ gap: 16 }}>
                    <div className="full-width">

                        <SelectAutocompleteMultiple
                            name={`onModel`}
                            label={'Resource Type'}
                            control={control}
                            options={taskOnModelList}
                            renderValue={(option) => option?.name}
                            handleChange={(option) => {
                                if (option?.id === taskOnModelEnums.FUNDER) {
                                    dispatch(fundingSourceActions.getFundingSource());
                                }
                            }}
                        />
                    </div>
                    <div className="full-width">

                        {watch('onModel') === taskOnModelEnums.FUNDER &&
                            <SelectAutocompleteMultiple
                                loadType={'GET_FUNDING_SOURCE'}
                                name={`resourceId`}
                                label={'Select Resource'}
                                control={control}
                                options={payerList?.length
                                    ? [{ id: 'All', name: 'None' }, ...payerList]
                                    : [{ id: 'All', name: 'None' }]
                                }
                                renderValue={(option) => option?.name}
                            />
                        }

                        {watch('onModel') && watch('onModel') !== taskOnModelEnums.FUNDER &&
                            <SelectAutocompletePagination
                                uiType={'fromForm'}
                                title={'Select Resource'}
                                name={'resourceId'}
                                handleSelect={(ev) => setValue('resourceId', ev?.target?.value)}
                                defaultValue={watch('resourceId')}
                                renderValue={(i) =>
                                    watch('onModel') === taskOnModelEnums.STAFF ? renderStaffName(i) :
                                        watch('onModel') === taskOnModelEnums.CLIENT ? renderClientName(i)
                                            : i?.name || ''
                                }
                                type={
                                    watch('onModel') === taskOnModelEnums.CLIENT ? 'client'
                                        : watch('onModel') === taskOnModelEnums.STAFF ? 'staff'
                                            : ''
                                }
                            />
                        }
                    </div>
                </div>
                <div className="flex-align-center" style={{ gap: 16 }}>
                    <div className="full-width">
                        <DateCalendarForm label={'Due Date'} name={'dueDate'} control={control} type={'modalInput'}
                                          noErrMessage={true} />
                        <div className="task-date-shortcuts">
                            <button type="button" className="task-date-shortcut-btn"
                                    onClick={() => handleDateShortcut(0)}>
                                Today
                            </button>
                            <button type="button" className="task-date-shortcut-btn"
                                    onClick={() => handleDateShortcut(1)}>
                                Tomorrow
                            </button>
                            <button type="button" className="task-date-shortcut-btn"
                                    onClick={() => handleDateShortcut(7)}>
                                Next Week
                            </button>
                        </div>
                    </div>
                    <div className="full-width" />
                </div>
                <div style={{ marginBottom: 24 }}>
                    {/* This whole block ASSIGNS tags to the task, which POST/PATCH /task routes through
                        tagService.addTag — a TAG_MANAGE action. TAG_READ alone must not see it, or the
                        form submits a `tags` body the API rejects with 403. On edit, `selectedTags` stays
                        seeded from the task, so hiding the picker leaves existing tags untouched on save. */}
                    {canManageTags() && (
                        <div style={{ marginTop: 20 }}>
                            <p className="task-tags-label">Tags</p>
                            <div className="task-tags-row">
                                <TagComponent module={tagEnums.TASK} notAllowAdd={true} notSetText={' '} />
                                {tagList?.length ? (
                                    tagList?.map((tag) => (
                                        <button
                                            key={tag.id}
                                            type="button"
                                            onClick={() => handleToggleTag(tag?.id)}
                                            className={`task-tag-chip ${selectedTags.includes(tag.id) ? 'task-tag-chip-selected' : ''}`}
                                            style={{
                                                background: renderColor(tag?.color).background,
                                                color: renderColor(tag?.color).color,
                                            }}
                                        >
                                            {tag.name}
                                        </button>
                                    ))
                                ) : (
                                    <p style={{ fontStyle: 14, marginTop: 2, color: '#51566D' }}>No Tags Yet</p>
                                )}
                            </div>
                        </div>
                    )}
                    {backError?.error && <p className="task-form-error">{backError.error}</p>}
                </div>
            </div>
            <CreateChancel
                type="submit"
                loader={!!loader.length}
                create={isEdit ? 'Save' : 'Create'}
                chancel={'Cancel'}
                onClose={close}
                buttonWidth="100%"
            />
        </form>
    );
};
